/**
 * ── A DIVISION SWITCH HANDS THE FEED BLOCKS BACK TO THE FEED (2026-09-28) ─────────────
 *
 * Found in the 2026-09-28 audit, driven on emailbuilder-dev: BOI · Grand Opening Thank You
 * filled five Boise floorplans (Donnelly 2800, Laurel Rv 2810 …); switching Step 1 to NSH
 * left all five Boise cards in the Nashville email — in the Step 1 preview, in the saved
 * draft, and so in what would have been submitted. A second pass showed the worse shape:
 * one card carrying a Nashville home PICK (`__homePick`) kept that home after switching
 * back to BOI, and because a pick scopes its section, the other four auto cards refilled
 * with Nashville floorplans too. A Boise email, five Nashville cards.
 *
 * Why nothing cleared it. `onDivisionChange` resets the Step 1 dependents (market,
 * community, masterplan, AOI, initiative) but never touched the block document. The fill
 * then treats what is on the cards as settled: a pick is the requester's choice, a card
 * already carrying a plan is not "pristine" so the excess trim leaves it, and a scope that
 * is still loading (or dry) writes nothing. Every one of those rules is right inside one
 * division and wrong across two — a pick names a community in the OLD division's
 * inventory, so it cannot be honoured in the new one.
 *
 * WHAT THIS DOES, per Featured card and the Listing table:
 *   · drops the per-division pick state — `__planPick`, `__homePick`, the send-time lot map
 *     (`__lots`), the feed's own ownership record (`__socFed`), the auto-expansion stamp
 *     (`__cardsAuto`) and the cards⇄table stash (`__layStash`, which holds the old
 *     division's rows and would bring them back on the next toggle);
 *   · puts every FEED-OWNED field (the fields any level of this block's map fills) back to
 *     the block's declared default, so the card is pristine again and the ordinary fill
 *     deals the new division's inventory into it — and trims or grows the card count to
 *     what that division can show, exactly as on a fresh template.
 *
 * WHAT IT LEAVES ALONE: anything the requester typed (`__socOff` — their claim on a field
 * is theirs, whatever division it was typed under), the button LABELS (not feed fields),
 * the card level (`__featLevel` — Plans stays Plans), and every block that is not a feed
 * block. Only a real switch runs it: FormView calls it from `onDivisionChange`, which a
 * draft resume or a duplicate load never goes through.
 */
import { echMeta, echNameOf } from "./echRegistry";
import { ECH_FEAT, dynMap, type DynBlock } from "./echDyn";

/** the blocks a division's inventory fills card by card */
export const isEchFeedCardBlock = (type: string): boolean => {
  const n = echNameOf(type);
  return /^ECH_Featured/.test(n) || n === "ECH_ListingTable";
};

/** the per-division state keys — cleared outright */
export const DIVISION_STATE_KEYS = ["__planPick", "__homePick", "__lots", "__socFed", "__cardsAuto", "__layStash"] as const;

/** every field ANY level of this block's feed map writes */
function feedFields(name: string): string[] {
  const out = new Set<string>();
  for (const lvl of Object.keys(ECH_FEAT) as (keyof typeof ECH_FEAT)[]) {
    const f = ECH_FEAT[lvl]?.fields?.[name];
    if (f) Object.keys(f).forEach((k) => out.add(k));
  }
  const m = dynMap(name);
  if (m) Object.keys(m).forEach((k) => out.add(k));
  return [...out];
}

/**
 * The patch that returns one feed block to the feed after a division switch, or null
 * when the block is not a feed block or is already pristine.
 */
export function echDivisionResetPatch(b: DynBlock): Record<string, unknown> | null {
  if (!b || !isEchFeedCardBlock(b.type)) return null;
  const d = (b.data || {}) as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  for (const k of DIVISION_STATE_KEYS) {
    const v = d[k];
    const has = Array.isArray(v) ? v.length > 0 : v != null && String(v).trim() !== "";
    if (has) patch[k] = "";
  }
  const claimed = new Set(String(d.__socOff || "").split(",").map((s) => s.trim()).filter(Boolean));
  const declared = echMeta(b.type)?.declared || {};
  for (const k of feedFields(echNameOf(b.type))) {
    if (claimed.has(k)) continue;                 /* the requester typed it — theirs */
    if (!(k in declared) && !(k in d)) continue;   /* a field this block never carried */
    const def = String((declared[k] as { default?: unknown } | undefined)?.default ?? "");
    const cur = d[k] == null ? "" : String(d[k]);
    if (cur !== def) patch[k] = def;
  }
  return Object.keys(patch).length ? patch : null;
}

/** the whole document after a division switch — unchanged blocks keep their identity */
export function echDivisionReset<T extends DynBlock>(blocks: T[]): T[] {
  let changed = false;
  const next = blocks.map((b) => {
    const p = echDivisionResetPatch(b);
    if (!p) return b;
    changed = true;
    return { ...b, data: { ...b.data, ...p } };
  });
  return changed ? next : blocks;
}
